import { useEffect, useState } from 'react';
import { Circle, Tooltip } from 'react-leaflet';
import type { Circle as LeafletCircle, PathOptions } from 'leaflet';
import { loadNeighborhoodPoints, isPlaceholderNeighborhoodPoints } from '@/geo/geoJsonLoader';
import type { NeighborhoodPoint } from '@/types/geography';
import type { ScoredGeography } from '@/types/insights';
import { colorForBand } from './scoreColor';
import { useUiStore } from '@/store/uiStore';

interface NeighborhoodLayerProps {
  geographies: ScoredGeography[];
}

// Meters, not pixels -- deliberately using react-leaflet's <Circle> (a real
// geographic circle) instead of <CircleMarker> (a fixed-PIXEL-radius dot).
// A pixel-radius marker stays the same tiny size on screen no matter how
// far you zoom in, so it visually reads as "the highlight disappeared"
// once you're at street level. A meter-radius circle keeps covering the
// same real ground area, so it grows on screen exactly the way the
// basemap's streets/buildings do -- staying visibly highlighted all the
// way to street-level zoom, which is the point of this layer.
const BASE_RADIUS_METERS = 450;
const HOVER_RADIUS_METERS = 600;

/**
 * Area-highlight layer for named neighborhoods in the four target metro
 * cores (Houston, Dallas-Fort Worth, Austin, San Antonio). Real neighborhood
 * *polygon* boundaries are deliberately not attempted anywhere in this app
 * -- OpenStreetMap's neighborhood-boundary coverage is inconsistent enough
 * city-to-city that a choropleth built on it would silently vary in
 * accuracy street-by-street. A fixed-radius circle per point (from
 * scripts/fetch-neighborhoods-osm.mjs) + real Zillow ZHVI data per point is
 * the same honest simplification CityLayer uses for cities (approximate
 * marker, not a traced boundary), just scaled to stay visible when you
 * zoom all the way in. Unlike CityLayer, each neighborhood has its OWN real
 * data (no parent-geography rollup) -- see zillowAdapter.ts.
 */
export function NeighborhoodLayer({ geographies }: NeighborhoodLayerProps) {
  const [points, setPoints] = useState<NeighborhoodPoint[] | null>(null);
  const selectGeo = useUiStore((s) => s.selectGeo);

  useEffect(() => {
    let cancelled = false;
    loadNeighborhoodPoints()
      .then((data) => {
        if (!cancelled) setPoints(data);
      })
      .catch((err) => console.error('[NeighborhoodLayer] failed to load points:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!points || isPlaceholderNeighborhoodPoints() || points.length === 0) return null;

  const byGeoId = new Map(geographies.map((g) => [g.record.geoId, g]));

  return (
    <>
      {points.map((point) => {
        const scored = byGeoId.get(point.geoId);
        const band = scored?.insights.hotColdScore;
        const baseStyle: PathOptions = {
          color: '#0f172a',
          weight: 1,
          fillColor: band ? colorForBand(band) : '#e2e8f0',
          fillOpacity: band ? 0.85 : 0.5,
        };
        return (
          <Circle
            key={point.geoId}
            center={[point.lat, point.lon]}
            radius={BASE_RADIUS_METERS}
            pathOptions={baseStyle}
            eventHandlers={{
              click: () => selectGeo(point.geoId),
              mouseover: (e) => {
                const marker = e.target as LeafletCircle;
                marker.setStyle({ weight: 3 });
                marker.setRadius(HOVER_RADIUS_METERS);
                marker.bringToFront();
              },
              mouseout: (e) => {
                const marker = e.target as LeafletCircle;
                marker.setStyle(baseStyle);
                marker.setRadius(BASE_RADIUS_METERS);
              },
            }}
          >
            <Tooltip className="map-hover-label" sticky opacity={1}>
              {scored
                ? `${point.name}, ${point.city} — ${scored.insights.hotColdScore.label} (${Math.round(scored.insights.hotColdScore.value)})`
                : `${point.name}, ${point.city} — no data for this period`}
            </Tooltip>
          </Circle>
        );
      })}
    </>
  );
}
